<script lang="ts" setup>
import CascadeBadge from '@/components/badges/CascadeBadge.vue'
import ProductsSelectTable, { ProductColumn } from '@/components/ProductsSelectTable.vue'
import { Heatpump } from '@/services/models/implementations/Heatpump.ts'
import projectStore from '@/store/ProjectStore.ts'
import formStore from '@/store/FormStore.ts'
import HeatPumpStats from '@/components/steps/heat-pumps/HeatPumpStats.vue'
import { computed } from 'vue'
import { RecommendationBadgeType, SalesPackageType } from '@/helper/Types.ts'
import { copyToClipboard, writeJsonToClipboard } from '@/helper/tools.ts'
import userStore from '@/store/UserStore.ts'
import HintBox from '@/components/HintBox.vue'
import { ProductResponse } from '@/services/models/ProductResponse.ts'
import InfoBadge from '@/components/badges/InfoBadge.vue'

const props = withDefaults(
  defineProps<{
    heatPump: Heatpump
    isSelected: boolean
  }>(),
  {
    isSelected: () => false
  }
)

const emit = defineEmits<{
  (e: 'selectAdditionalProduct', additionalProductId: string): void
  (e: 'onSelectHeatPump'): void
}>()

const additionalProductValue = computed((): string => {
  if (!props.isSelected) {
    return ''
  }

  return (formStore.data.additionalProductId || formStore.data.applianceInterfaceId || '').toString()
})

const selectAdditionalProduct = (additionalProductId: string): void => {
  emit('selectAdditionalProduct', additionalProductId)
}

const additionalProductsColumns: ProductColumn[] = [
  {
    columnName: 'step_components.table_product',
    cssClass: 'is--product',
    valCb: (val: ProductResponse) => val.name
  }
]
</script>

<template>
  <article
    data-hj-whitelist
    :data-id="`${heatPump.id}.${heatPump.chr.cascadeSize}`"
    class="component select-card"
    @click.exact="$emit('onSelectHeatPump')"
    @click.exact.alt.prevent="copyToClipboard(heatPump.id)"
    @click.exact.alt.ctrl.shift.prevent="writeJsonToClipboard(heatPump)"
  >
    <CascadeBadge
      v-if="heatPump.chr.cascadeSize > 1"
      :product-title="heatPump.marketingName"
      :quantity="heatPump.chr.cascadeSize"
    />

    <input
      :checked="isSelected"
      :name="`${heatPump.id}.${heatPump.chr.cascadeSize}`"
      :value="heatPump.id"
      type="radio"
    >
    <label :for="`${heatPump.id}.${heatPump.chr.cascadeSize}`" class="select-card__label" />

    <div class="component__image-box select-card__image-box image-box is--cover">
      <div :class="{ 'fallback-bg': !heatPump.pictureUrl }" class="component__image image-box__container">
        <img v-if="heatPump.pictureUrl" :alt="heatPump.name" :src="heatPump.pictureUrl" loading="lazy">
      </div>
    </div>

    <div class="component__content">
      <div class="name-badge__wrapper">
        <span class="block component__name product__title">{{ heatPump.name }}</span>
        <div v-if="heatPump.salesPackageType === SalesPackageType.SALES_PACKAGE" style="position: relative; z-index: 100;">
          <InfoBadge
            :info-text="$t('step_sale_set.badge_set')"
            :tooltip-text="$t('step_sale_set.badge_set_tooltip')"
          />
        </div>
      </div>
      <span class="block component__type">
        {{ $t('components.type_heat_pump_' + projectStore.projectProperties.heatSource.val?.toString().split('_')[0]) }}
      </span>
      <ul v-if="heatPump.productAdvantages" class="check-list product__features">
        <li v-for="index in Math.min(3, heatPump.productAdvantages.length)" :key="index">
          {{ heatPump.productAdvantages[index - 1] }}
        </li>
      </ul>

      <HintBox
        v-if="projectStore.heatingFlowTemperature !== heatPump.chr.heating?.operatingPoint?.flowTemperature
          && userStore.heatPumpSuggestionHybridFlowTemperature !== heatPump.chr.heating?.operatingPoint?.flowTemperature"
        :description="$t('step_heat_pumps.flow_temperature_not_sufficient')" style="margin-top: 1rem;" />

      <HeatPumpStats v-if="userStore.heatPumpStatsConfig.length" :heat-pump="heatPump" />

      <ProductsSelectTable
        v-if="heatPump.chr.additionalProducts?.length"
        :columns="additionalProductsColumns"
        :details-in-modal="true"
        :has-no-header="true"
        :model-value="additionalProductValue"
        :product-name="'additional-product-' + heatPump.id + '-' + heatPump.chr.cascadeSize"
        :products="heatPump.chr.additionalProducts"
        :recommendation-type="RecommendationBadgeType.TOP"
        :unselectable="false"
        class="is--add-product-table"
        @update:model-value="selectAdditionalProduct"
      />
    </div>
  </article>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

.select-card {

  @include media('<tablet') {
    flex-direction: column;

    .component__content {
      max-width: 100%;
    }

    .image-box__container {
      position: relative;
      @include media('<tablet') {
        min-height: 1px;
      }
    }

    .select-card__image-box {
      @include themify($themes) {
        border-top-left-radius: rem-calc(themed('cardBorderRadius'));
        border-top-right-radius: rem-calc(themed('cardBorderRadius'));
        border-bottom-left-radius: 0;
      }
    }
  }

  .fTable.is--add-product-table {
    @include themify($themes) {
      border-radius: rem-calc(themed('cardBorderRadius'));
    }
  }
}

.fTable.is--add-product-table {
  margin-top: 1rem;
  border: 1px solid $borderColor;
  z-index: 1;
  position: relative;

  .fTable__item {
    &:last-child {
      border-bottom: none;
    }
  }

  .column__controls {
    flex-grow: 0;
    flex-shrink: 0;
    flex-basis: rem-calc(102);
  }
}

.name-badge__wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}
</style>
